import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { contractsApi, blocksApi, sectionsApi, systemVariablesApi } from '../services/api';
import { Contract, Block, Section, ContractBlock, Variable, SystemVariable, HeaderFooterConfig } from '../types';
import HeaderFooterEditor from '../components/HeaderFooterEditor';
import DocumentDesigner,{DesignerDocument} from '../components/DocumentDesigner';
import SystemVariableBlock from '../components/SystemVariableBlock';
import {
  Save,
  Check,
  ArrowLeft,
  ArrowRight,
  GripVertical,
  Plus,
  Trash2,
  AlertCircle,
  FileText,
  Eye,
  Layers,
  FormInput,
  Variable as VariableIcon,
  Settings,
  Info
} from 'lucide-react';

// Phase Stepper Component
function PhaseStepper({ currentPhase, onPhaseChange }: { currentPhase: string; onPhaseChange: (phase: string) => void }) {
  const phases = [
    { id: 'BLOCKS', label: 'Bauteile auswählen', icon: Layers },
    { id: 'VARIABLES', label: 'Variablen ausfüllen', icon: FormInput },
  ];

  return (
    <div className="flex items-center justify-center mb-6">
      {phases.map((phase, index) => {
        const isActive = phase.id === currentPhase;
        const isPast = phases.findIndex(p => p.id === currentPhase) > index;
        const Icon = phase.icon;

        return (
          <React.Fragment key={phase.id}>
            <button
              onClick={() => isPast && onPhaseChange(phase.id)}
              disabled={!isPast}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                isActive
                  ? 'bg-primary-600 text-white'
                  : isPast
                  ? 'bg-primary-100 text-primary-700 hover:bg-primary-200 cursor-pointer'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Icon className="h-5 w-5 mr-2" />
              <span className="font-medium">{phase.label}</span>
            </button>
            {index < phases.length - 1 && (
              <ArrowRight className={`h-5 w-5 mx-2 ${isPast ? 'text-primary-600' : 'text-gray-300'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Sortable Block Item Component (für Phase 1)
function SortableBlockItem({
  contractBlock,
  block,
  onRemove,
  systemVariables
}: {
  contractBlock: ContractBlock;
  block: Block;
  onRemove: () => void;
  systemVariables?: SystemVariable[];
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: contractBlock.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const isSystemVariable = block.blockType === 'SYSTEM_VARIABLE' || contractBlock.systemVariableId;
  const systemVar = systemVariables?.find(v => v.id === contractBlock.systemVariableId);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border shadow-sm p-4 flex items-center ${
        isSystemVariable 
          ? 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200' 
          : 'bg-white border-gray-200'
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="p-1 mr-3 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="h-5 w-5" />
      </button>
      <div className="flex-1">
        {isSystemVariable ? (
          <>
            <div className="flex items-center gap-2">
              <VariableIcon className="h-4 w-4 text-purple-600" />
              <h4 className="font-medium text-purple-900">
                {systemVar?.name || 'Vertragsvariable'}
              </h4>
            </div>
            <p className="text-xs text-purple-600 mt-1">
              Beispiel: {systemVar?.example || '...'}
            </p>
          </>
        ) : (
          <>
            <h4 className="font-medium text-gray-900">{block.name}</h4>
            <p className="text-xs text-gray-500">{block.section?.name}</p>
            {block.variables.length > 0 && (
              <p className="text-xs text-primary-600 mt-1">
                {block.variables.length} Variable(n)
              </p>
            )}
          </>
        )}
      </div>
      <button
        onClick={onRemove}
        className="p-1 text-gray-400 hover:text-red-500"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

// Variable Input Component mit Tooltip-Unterstützung
function VariableInput({
  variable,
  value,
  onChange
}: {
  variable: Variable;
  value: string;
  onChange: (value: string) => void;
}) {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const inputClassName = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

  // Parse options - unterstützt sowohl JSON als auch kommagetrennte Strings
  const getSelectOptions = (): string[] => {
    if (!variable.options) return [];
    
    try {
      // Versuche JSON zu parsen
      const parsed = JSON.parse(variable.options);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      // Falls kein JSON, als kommagetrennte Liste behandeln
      return variable.options.split(',').map(opt => opt.trim()).filter(opt => opt);
    }
  };

  return (
    <div className="mb-4">
      <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
        <span>{variable.label}</span>
        {variable.required && <span className="text-red-500 ml-1">*</span>}
        {variable.helpText && (
          <div className="relative ml-1.5">
            <button
              type="button"
              className="text-gray-400 hover:text-primary-500 focus:outline-none"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
            >
              <Info className="h-4 w-4" />
            </button>
            {showTooltip && (
              <div className="absolute z-50 left-6 top-0 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                {variable.helpText}
                <div className="absolute left-0 top-2 -ml-1 w-2 h-2 bg-gray-900 rotate-45" />
              </div>
            )}
          </div>
        )}
      </label>
      
      {variable.valueType === 'TEXT' && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputClassName}
          placeholder={variable.defaultValue || ''}
        />
      )}
      {variable.valueType === 'NUMBER' && (
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputClassName}
          placeholder={variable.defaultValue || ''}
        />
      )}
      {variable.valueType === 'DATE' && (
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputClassName}
        />
      )}
      {variable.valueType === 'CURRENCY' && (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">€</span>
          <input
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`${inputClassName} pl-8`}
            placeholder={variable.defaultValue || '0.00'}
          />
        </div>
      )}
      {variable.valueType === 'BOOLEAN' && (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputClassName}
        >
          <option value="">Bitte wählen</option>
          <option value="Ja">Ja</option>
          <option value="Nein">Nein</option>
        </select>
      )}
      {variable.valueType === 'SELECT' && (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={inputClassName}
        >
          <option value="">Bitte wählen</option>
          {getSelectOptions().map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      )}
    </div>
  );
}

// Main Contract Editor Component
export default function ContractEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contract, setContract] = useState<Contract | null>(null);
  const [availableBlocks, setAvailableBlocks] = useState<Block[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [contractBlocks, setContractBlocks] = useState<ContractBlock[]>([]);
  const [variableValues, setVariableValues] = useState<Record<string, Record<string, string>>>({});
  const [currentPhase, setCurrentPhase] = useState<string>('BLOCKS');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [structuredDocument,setStructuredDocument]=useState<DesignerDocument|null>(null);
  
  // Header/Footer state
  const [headerConfig, setHeaderConfig] = useState<HeaderFooterConfig | null>(null);
  const [footerConfig, setFooterConfig] = useState<HeaderFooterConfig | null>(null);
  const [showHeaderFooterPanel, setShowHeaderFooterPanel] = useState(false);
  
  // Headline state (große Überschrift auf der ersten Seite)
  const [headline, setHeadline] = useState<string>('');
  
  // System Variables state
  const [systemVariables, setSystemVariables] = useState<SystemVariable[]>([]);
  const [showSystemVariablePanel, setShowSystemVariablePanel] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      const [contractRes, blocksRes, sectionsRes, sysVarsRes] = await Promise.all([
        contractsApi.getById(id!),
        blocksApi.getAll({ active: true }),
        sectionsApi.getAll(),
        systemVariablesApi.getAll()
      ]);

      setContract(contractRes.contract);
      setAvailableBlocks(blocksRes.blocks);
      setSections(sectionsRes.sections);
      setSystemVariables(sysVarsRes.variables);
      setCurrentPhase(contractRes.contract.phase || 'BLOCKS');
      if(contractRes.contract.documentJson){try{setStructuredDocument(JSON.parse(contractRes.contract.documentJson))}catch{setError('Strukturiertes Vertragsdokument ist beschädigt')}}

      // Header/Footer Config laden
      if (contractRes.contract.headerConfig) {
        try {
          setHeaderConfig(JSON.parse(contractRes.contract.headerConfig));
        } catch (e) {}
      }
      if (contractRes.contract.footerConfig) {
        try {
          setFooterConfig(JSON.parse(contractRes.contract.footerConfig));
        } catch (e) {}
      }
      
      // Headline laden
      if (contractRes.contract.headline) {
        setHeadline(contractRes.contract.headline);
      }

      const blocks = contractRes.contract.blocks || [];
      setContractBlocks(blocks);

      // Variablenwerte aus gespeicherten Daten oder Entwurf laden
      const values: Record<string, Record<string, string>> = {};
      
      // Zuerst aus Entwurf laden falls vorhanden
      if (contractRes.contract.variablesDraft?.draftData) {
        const draftData = JSON.parse(contractRes.contract.variablesDraft.draftData);
        Object.assign(values, draftData);
      }
      
      // Dann gespeicherte Werte übernehmen
      blocks.forEach(cb => {
        if (!values[cb.id]) values[cb.id] = {};
        cb.values?.forEach(v => {
          values[cb.id][v.variableId] = v.value;
        });
      });
      
      setVariableValues(values);
    } catch (err) {
      setError('Fehler beim Laden des Vertrags');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setContractBlocks((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleAddBlock = (block: Block) => {
    const newContractBlock: ContractBlock = {
      id: `temp-${Date.now()}`,
      contractId: id!,
      blockId: block.id,
      sortOrder: contractBlocks.length,
      block
    };
    setContractBlocks([...contractBlocks, newContractBlock]);
    setVariableValues({ ...variableValues, [newContractBlock.id]: {} });
  };

  const handleRemoveBlock = (contractBlockId: string) => {
    setContractBlocks(contractBlocks.filter(cb => cb.id !== contractBlockId));
    const newValues = { ...variableValues };
    delete newValues[contractBlockId];
    setVariableValues(newValues);
  };

  const handleVariableChange = (contractBlockId: string, variableId: string, value: string) => {
    setVariableValues({
      ...variableValues,
      [contractBlockId]: {
        ...variableValues[contractBlockId],
        [variableId]: value
      }
    });
  };

  // System Variable Block hinzufügen
  const handleAddSystemVariableBlock = (variableId: string) => {
    // Wir brauchen einen speziellen "System Variable" Block
    // Finde oder erstelle einen Block für System-Variablen
    const systemVarBlock = availableBlocks.find(b => b.blockType === 'SYSTEM_VARIABLE');
    
    if (!systemVarBlock) {
      // Fallback: Erstelle einen virtuellen Block
      const variable = systemVariables.find(v => v.id === variableId);
      const newContractBlock: ContractBlock = {
        id: `temp-sys-${Date.now()}`,
        contractId: id!,
        blockId: 'system-variable', // Spezielle ID für System-Variable
        sortOrder: contractBlocks.length,
        systemVariableId: variableId,
        customContent: `{{${variableId}}}`,
        block: {
          id: 'system-variable',
          name: `Vertragsvariable: ${variable?.name || variableId}`,
          content: `{{${variableId}}}`,
          blockType: 'SYSTEM_VARIABLE',
          sectionId: sections[0]?.id || '',
          isActive: true,
          variables: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      };
      setContractBlocks([...contractBlocks, newContractBlock]);
    } else {
      const newContractBlock: ContractBlock = {
        id: `temp-sys-${Date.now()}`,
        contractId: id!,
        blockId: systemVarBlock.id,
        sortOrder: contractBlocks.length,
        systemVariableId: variableId,
        block: systemVarBlock
      };
      setContractBlocks([...contractBlocks, newContractBlock]);
    }
    
    setShowSystemVariablePanel(false);
  };

  // Header/Footer speichern
  const saveHeaderFooter = async () => {
    setIsSaving(true);
    setError('');
    
    try {
      await contractsApi.updateHeaderFooter(id!, { headerConfig: headerConfig || undefined, footerConfig: footerConfig || undefined });
      setSuccessMessage('Kopf-/Fußzeilen gespeichert');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  // Headline speichern
  const saveHeadline = async () => {
    setIsSaving(true);
    setError('');
    
    try {
      await contractsApi.updateHeadline(id!, headline);
      setSuccessMessage('Überschrift gespeichert');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  // Blöcke speichern und zur Variablen-Phase wechseln
  const proceedToVariables = async () => {
    if (contractBlocks.length === 0 && !structuredDocument?.nodes.length) {
      setError('Fügen Sie mindestens einen Bauteil hinzu');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      if(structuredDocument){const saved=await contractsApi.updateDocument(id!,structuredDocument,contract?.revision||1);setContract(current=>current?{...current,revision:saved.revision}:current);}
      const blocks = contractBlocks.map((cb, index) => ({
        blockId: cb.blockId,
        sortOrder: index,
        systemVariableId: cb.systemVariableId,
        customContent: cb.customContent
      }));

      await contractsApi.updateBlocks(id!, blocks);
      await contractsApi.proceedToVariables(id!);
      
      // Daten neu laden um korrekte IDs zu bekommen
      await loadData();
      setCurrentPhase('VARIABLES');
      setSuccessMessage('Blöcke gespeichert. Füllen Sie nun die Variablen aus.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  // Variablen zwischenspeichern
  const saveVariablesDraft = async () => {
    setIsSaving(true);
    setError('');

    try {
      await contractsApi.saveVariablesDraft(id!, variableValues);
      setSuccessMessage('Entwurf gespeichert');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  // Variablen finalisieren
  const finalizeContract = async () => {
    setIsSaving(true);
    setError('');

    try {
      if(structuredDocument){const saved=await contractsApi.updateDocument(id!,structuredDocument,contract?.revision||1);setContract(current=>current?{...current,revision:saved.revision}:current);await contractsApi.finalizeDocument(id!);}else await contractsApi.finalizeVariables(id!, variableValues);
      setSuccessMessage('Vertrag finalisiert');
      setTimeout(() => navigate(`/contracts/${id}`), 1500);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Finalisieren fehlgeschlagen');
      if (err.response?.data?.missingVariables) {
        setError(`Fehlende Pflichtfelder: ${err.response.data.missingVariables.join(', ')}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const filteredBlocks = selectedSection
    ? availableBlocks.filter(b => b.sectionId === selectedSection)
    : availableBlocks;

  // Variablen nach Abteilen gruppieren
  const getGroupedVariables = () => {
    const groups: { section: Section | { id: string; name: string }; blocks: { contractBlock: ContractBlock; block: Block }[] }[] = [];
    
    const sectionMap = new Map<string, { contractBlock: ContractBlock; block: Block }[]>();
    
    for (const cb of contractBlocks) {
      // Block-Daten aus contractBlocks oder aus availableBlocks holen
      let blockData = cb.block;
      if (!blockData && cb.blockId) {
        blockData = availableBlocks.find(b => b.id === cb.blockId);
      }
      
      if (!blockData) {
        console.warn('Block nicht gefunden für ContractBlock:', cb);
        continue;
      }
      
      const sectionId = blockData.sectionId || '__none__';
      if (!sectionMap.has(sectionId)) {
        sectionMap.set(sectionId, []);
      }
      sectionMap.get(sectionId)!.push({ contractBlock: cb, block: blockData });
    }

    // Zuerst Blöcke ohne Abteil hinzufügen
    if (sectionMap.has('__none__')) {
      groups.push({
        section: { id: '__none__', name: 'Allgemein (ohne Abteil)' },
        blocks: sectionMap.get('__none__')!
      });
    }

    // Dann Blöcke nach Abteilen sortiert
    for (const section of sections) {
      if (sectionMap.has(section.id)) {
        groups.push({
          section,
          blocks: sectionMap.get(section.id)!
        });
      }
    }

    return groups;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-gray-900">Vertrag nicht gefunden</h2>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 -mx-4 sm:-mx-6 lg:-mx-8 -mt-8 mb-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/contracts')}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{contract.title}</h1>
                <p className="text-sm text-gray-500">Vertragseditor - Version {contract.version}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(`/contracts/${id}`)}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Eye className="h-4 w-4 mr-2" />
                Vorschau
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Phase Stepper */}
      <PhaseStepper 
        currentPhase={currentPhase} 
        onPhaseChange={(phase) => {
          if (phase === 'BLOCKS') {
            setCurrentPhase('BLOCKS');
          }
        }} 
      />

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
          <Check className="h-5 w-5 mr-2 flex-shrink-0" />
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage('')} className="ml-auto text-green-500 hover:text-green-700">×</button>
        </div>
      )}

      {/* Header/Footer & Settings Panel */}
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setShowHeaderFooterPanel(!showHeaderFooterPanel)}
          className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border ${
            showHeaderFooterPanel ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Settings className="h-4 w-4 mr-2" />
          Überschrift & Kopf-/Fußzeilen
        </button>
      </div>

      {/* Header/Footer Configuration Panel */}
      {showHeaderFooterPanel && (
        <div className="mb-6 space-y-4">
          {/* Headline Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Vertragsüberschrift (H1)</h3>
                <p className="text-xs text-gray-500">Große Überschrift auf der ersten Seite (z.B. "MIETVERTRAG")</p>
              </div>
              <button
                onClick={saveHeadline}
                disabled={isSaving}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-1" />
                Speichern
              </button>
            </div>
            <input
              type="text"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="z.B. MIETVERTRAG, ARBEITSVERTRAG, KAUFVERTRAG..."
              className="w-full px-4 py-3 text-xl font-bold text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase tracking-wide"
            />
          </div>

          {/* Header/Footer Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900">Kopf- und Fußzeilen konfigurieren</h3>
              <button
                onClick={saveHeaderFooter}
                disabled={isSaving}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4 mr-1" />
                Speichern
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <HeaderFooterEditor
                type="header"
                config={headerConfig}
                onChange={setHeaderConfig}
                contractId={id}
                contractBlocks={contractBlocks}
              />
              <HeaderFooterEditor
                type="footer"
                config={footerConfig}
                onChange={setFooterConfig}
                contractId={id}
                contractBlocks={contractBlocks}
              />
            </div>
          </div>
        </div>
      )}

      {currentPhase==='BLOCKS'&&structuredDocument&&<div className="mb-6"><div className="flex justify-between mb-3"><div><h2 className="text-lg font-semibold">Vertragsdokument bearbeiten</h2><p className="text-sm text-gray-500">Die Vorlage wurde als unabhängige Vertragsfassung übernommen.</p></div><button className="px-4 py-2 bg-primary-600 text-white rounded-lg" onClick={async()=>{try{const result=await contractsApi.updateDocument(id!,structuredDocument,contract?.revision||1);setContract(current=>current?{...current,revision:result.revision}:current);setSuccessMessage('Dokument gespeichert')}catch(err:any){setError(err.response?.data?.error||'Dokument konnte nicht gespeichert werden')}}}>Dokument speichern</button></div><DocumentDesigner value={structuredDocument} onChange={setStructuredDocument} blocks={availableBlocks} variables={Array.from(new Set(availableBlocks.flatMap(block=>(block.variables||[]).map(variable=>variable.name))))}/></div>}

      {/* Phase 1: Blöcke auswählen */}
      {currentPhase === 'BLOCKS' && (
        <div className="flex gap-6 flex-1">
          {/* Contract Blocks */}
          <div className="flex-1">
            <div className="bg-gray-50 rounded-lg p-4 min-h-[400px]">
              <h3 className="text-sm font-medium text-gray-700 mb-4">
                Ausgewählte Bauteile ({contractBlocks.length})
              </h3>

              {contractBlocks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Wählen Sie Bauteile aus der rechten Liste aus</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={contractBlocks.map(cb => cb.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {contractBlocks.map((cb) => (
                        <SortableBlockItem
                          key={cb.id}
                          contractBlock={cb}
                          block={cb.block!}
                          onRemove={() => handleRemoveBlock(cb.id)}
                          systemVariables={systemVariables}
                        />
                      ))}
                    </div>
                  </SortableContext>
                  <DragOverlay>
                    {activeId ? (
                      <div className="bg-white rounded-lg border-2 border-primary-500 shadow-lg p-4 opacity-90">
                        <p className="font-medium">
                          {contractBlocks.find(cb => cb.id === activeId)?.block?.name}
                        </p>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              )}

              {/* Action Button */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={proceedToVariables}
                  disabled={isSaving || (contractBlocks.length === 0 && !structuredDocument?.nodes.length)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {isSaving ? 'Wird gespeichert...' : 'Weiter zu Variablen'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </button>
              </div>
            </div>
          </div>

          {/* Available Blocks Sidebar */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg border border-gray-200 p-4 sticky top-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Verfügbare Bauteile</h3>

              {/* System Variable Block */}
              <div className="mb-4">
                <button
                  onClick={() => setShowSystemVariablePanel(!showSystemVariablePanel)}
                  className="w-full p-3 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg hover:border-purple-300 transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-purple-100 rounded">
                      <VariableIcon className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium text-purple-900 text-sm">Vertragsvariable</div>
                      <div className="text-xs text-purple-600">Automatisch befüllt</div>
                    </div>
                  </div>
                </button>
                
                {showSystemVariablePanel && (
                  <div className="mt-2 p-3 border border-purple-200 rounded-lg bg-white">
                    <p className="text-xs text-gray-600 mb-2">Wählen Sie eine System-Variable:</p>
                    <SystemVariableBlock
                      onChange={(variableId) => handleAddSystemVariableBlock(variableId)}
                    />
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4">
                <select
                  value={selectedSection}
                  onChange={(e) => setSelectedSection(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Alle Abteile</option>
                  {sections.map((section) => (
                    <option key={section.id} value={section.id}>{section.name}</option>
                  ))}
                </select>

                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {filteredBlocks.filter(b => b.blockType !== 'SYSTEM_VARIABLE').map((block) => (
                    <div
                      key={block.id}
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900 truncate">{block.name}</h4>
                          <p className="text-xs text-gray-500">{block.section?.name}</p>
                          {block.variables.length > 0 && (
                            <p className="text-xs text-primary-600 mt-1">{block.variables.length} Variable(n)</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleAddBlock(block)}
                          className="ml-2 p-1.5 text-primary-600 hover:bg-primary-100 rounded"
                          title="Hinzufügen"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phase 2: Variablen ausfüllen */}
      {currentPhase === 'VARIABLES' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Variablen ausfüllen</h3>
            <p className="text-sm text-gray-500 mb-6">
              Füllen Sie alle Pflichtfelder aus. Sie können jederzeit zwischenspeichern und später fortfahren.
            </p>

            {/* Debug Info entfernen nach Fehlerbehebung */}
            {contractBlocks.length === 0 && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  Keine Bauteile im Vertrag. Gehen Sie zurück und fügen Sie Bauteile hinzu.
                </p>
              </div>
            )}

            {(() => {
              const groupedVars = getGroupedVariables();
              const totalVariables = groupedVars.reduce((sum, g) => 
                sum + g.blocks.reduce((bSum, b) => bSum + (b.block.variables?.length || 0), 0), 0);
              
              if (contractBlocks.length > 0 && totalVariables === 0) {
                return (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      Die ausgewählten Bauteile enthalten keine Variablen. Sie können den Vertrag direkt finalisieren.
                    </p>
                  </div>
                );
              }
              
              return groupedVars.map(({ section, blocks }) => {
                // Nur Abschnitte mit Variablen anzeigen
                const blocksWithVars = blocks.filter(({ block }) => (block.variables?.length || 0) > 0);
                if (blocksWithVars.length === 0) return null;
                
                return (
                  <div key={section.id} className="mb-8">
                    <h4 className="text-md font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">
                      {section.name}
                    </h4>
                    
                    {blocksWithVars.map(({ contractBlock, block }) => {
                      const variables = block.variables || [];
                      
                      return (
                        <div key={contractBlock.id} className="mb-6 pl-4 border-l-2 border-gray-200">
                          <h5 className="text-sm font-medium text-gray-700 mb-3">{block.name}</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {variables
                              .sort((a, b) => a.sortOrder - b.sortOrder)
                              .map((variable) => (
                                <VariableInput
                                  key={variable.id}
                                  variable={variable}
                                  value={variableValues[contractBlock.id]?.[variable.id] || ''}
                                  onChange={(value) => handleVariableChange(contractBlock.id, variable.id, value)}
                                />
                              ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })()}

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                onClick={() => setCurrentPhase('BLOCKS')}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück zu Bauteilen
              </button>
              
              <div className="flex space-x-3">
                <button
                  onClick={saveVariablesDraft}
                  disabled={isSaving}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Zwischenspeichern
                </button>
                <button
                  onClick={finalizeContract}
                  disabled={isSaving}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  <Check className="h-4 w-4 mr-2" />
                  Finalisieren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
